import tilatinaLogo from "@/assets/tilatina-logo.jpg";
import { type FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { AROCIcon } from "@/components/AROCIcon";
import { TotpVerifyInput } from "@/components/TotpVerifyInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";

type Step = "credentials" | "verify-2fa";

export function LoginPage() {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<Step>("credentials");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === "authenticated") {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!username.trim() || !password) {
      setError("Completa usuario y contraseña.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await login(username.trim(), password);
      if (result === "ok") {
        navigate("/", { replace: true });
      } else if (result === "requires_2fa") {
        setStep("verify-2fa");
      } else if (result === "requires_2fa_setup") {
        navigate("/account/security/setup-forced", { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Credenciales inválidas.");
      } else if (err instanceof ApiError && err.status === 429) {
        setError("Demasiados intentos. Intenta en 1 minuto.");
      } else {
        setError("No se pudo conectar con el backend.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Columna izquierda — branding (solo desktop) */}
      <div className="hidden lg:flex w-1/2 flex-col items-center justify-center bg-white border-r border-border p-12 gap-24">
        <div className="flex flex-col items-center gap-5">
          <AROCIcon size={96} />
          <div className="flex flex-col items-center gap-1">
            <div className="text-5xl font-bold tracking-wide text-slate-900">AROC</div>
            <div className="text-[10px] font-medium tracking-[0.16em] text-slate-500 uppercase">
              Interfaz Operativa
            </div>
          </div>
          <div className="text-[11px] font-medium tracking-[0.14em] text-slate-400 uppercase">
            SEES · THINKS · ACTS · AIOPS + SOAR
          </div>
        </div>
        <div className="flex flex-col items-center gap-3">
          <div className="text-[10px] font-medium tracking-[0.16em] text-slate-400 uppercase">
            OPERADO PARA
          </div>
          <img
            src={tilatinaLogo}
            alt="tiLatina"
            className="h-40 w-auto"
          />
        </div>
      </div>

      {/* Columna derecha — formulario */}
      <div className="flex flex-1 items-center justify-center bg-muted/30 p-8">
        <div className="w-full max-w-sm">
          {/* Branding AROC en mobile (oculto en desktop) */}
          <div className="flex flex-col items-center gap-2 mb-8 lg:hidden">
            <AROCIcon size={64} />
            <div className="text-3xl font-medium tracking-wide text-foreground leading-none">
              AROC
            </div>
            <div className="text-[11px] font-medium tracking-[0.18em] text-blue-600 uppercase dark:text-blue-400">
              Auto-Response Operations Center
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">Interfaz Operativa</div>
          </div>
          <div className="mb-5">
            <div className="text-xl font-semibold text-foreground">Iniciar sesión</div>
            <div className="text-sm text-muted-foreground mt-1">
              Accede al centro de operaciones de respuesta automática.
            </div>
          </div>
          <Card>
            <CardContent className="pt-6">
              {step === "credentials" ? (
                <form className="space-y-4" onSubmit={onSubmit} noValidate>
                  <div className="space-y-2">
                    <Label htmlFor="username">Usuario</Label>
                    <Input
                      id="username"
                      autoComplete="username"
                      autoFocus
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={submitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Contraseña</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={submitting}
                    />
                  </div>
                  {error && (
                    <div
                      role="alert"
                      className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
                    >
                      {error}
                    </div>
                  )}
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? "Entrando…" : "Entrar"}
                  </Button>
                </form>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Ingresa el código de verificación de tu app TOTP.
                  </p>
                  <TotpVerifyInput
                    onSuccess={() => {
                      navigate("/", { replace: true });
                    }}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
